/**
 * Pure layout for the Hierarchy graph — positions only, no xyflow import, so
 * it is unit-testable and the xyflow chunk stays lazy.
 *
 * Tidy top-down tree: a post-order walk gives every leaf the next free
 * horizontal slot, a parent is centered over its first and last child, and
 * `y = depth * (NODE_H + GAP_Y)`. Collapsed nodes are leaves (their subtree is
 * not laid out); a parent with more than `HIERARCHY_OVERFLOW_CHILDREN` children
 * shows the first N (always including the current account's ancestor path)
 * plus a trailing "+N accounts" node.
 */
import {
  visibleChildren,
  type HierarchyTree,
} from '@shared/flow/account-hierarchy-tree';

export const NODE_W = 240;
export const NODE_H = 96; // 3 text lines (+ the indicator rides the owner line); keep fixed
/** Card height while the deal overlay is on (adds the status chips + amount lines). */
export const NODE_H_OVERLAY = 124;
/** xyflow's opt-outs so a click/drag on interactive content inside a node or edge label never pans the canvas. */
export const XYFLOW_INTERACTIVE = 'nodrag nopan';
export const GAP_X = 24;
export const GAP_Y = 56;

export type HierarchyLayoutNode =
  | { kind: 'account'; id: string; x: number; y: number; depth: number; hasChildren: boolean; expanded: boolean }
  | { kind: 'more'; id: string; x: number; y: number; depth: number; parentId: string; hiddenCount: number };

export interface HierarchyLayoutEdge {
  id: string;
  source: string;
  target: string;
}

export interface HierarchyLayout {
  nodes: HierarchyLayoutNode[];
  edges: HierarchyLayoutEdge[];
  width: number;
  height: number;
  /** The card height every row was spaced by. */
  nodeHeight: number;
}

export interface ComputeHierarchyLayoutInput {
  tree: HierarchyTree;
  expandedIds: ReadonlySet<string>;
  revealedIds: ReadonlySet<string>;
  currentId: string;
  /** Uniform card height (default `NODE_H`); rows are spaced by it. */
  nodeHeight?: number;
}

export const moreNodeId = (parentId: string): string => `more:${parentId}`;

export function computeHierarchyLayout({ tree, expandedIds, revealedIds, currentId, nodeHeight = NODE_H }: ComputeHierarchyLayoutInput): HierarchyLayout {
  const nodes: HierarchyLayoutNode[] = [];
  const edges: HierarchyLayoutEdge[] = [];
  if (!tree.byId.has(tree.rootId)) return { nodes, edges, width: 0, height: 0, nodeHeight };

  let nextSlot = 0;
  let maxDepth = 0;

  // Returns this subtree's horizontal slot (in slot units, may be fractional).
  const place = (id: string, depth: number): number => {
    maxDepth = Math.max(maxDepth, depth);
    const kids = tree.childrenOf.get(id) ?? [];
    const expanded = kids.length > 0 && expandedIds.has(id);
    const childSlots: number[] = [];
    let hiddenCount = 0;
    if (expanded) {
      const vis = visibleChildren(tree, id, currentId, revealedIds);
      hiddenCount = vis.hiddenCount;
      for (const k of vis.shown) {
        childSlots.push(place(k.id, depth + 1));
        edges.push({ id: `${id}->${k.id}`, source: id, target: k.id });
      }
      if (hiddenCount > 0) {
        maxDepth = Math.max(maxDepth, depth + 1);
        const slot = nextSlot++;
        childSlots.push(slot);
        nodes.push({ kind: 'more', id: moreNodeId(id), x: slot * (NODE_W + GAP_X), y: (depth + 1) * (nodeHeight + GAP_Y), depth: depth + 1, parentId: id, hiddenCount });
        edges.push({ id: `${id}->${moreNodeId(id)}`, source: id, target: moreNodeId(id) });
      }
    }
    const slot = childSlots.length > 0 ? (childSlots[0] + childSlots[childSlots.length - 1]) / 2 : nextSlot++;
    nodes.push({
      kind: 'account', id, x: slot * (NODE_W + GAP_X), y: depth * (nodeHeight + GAP_Y),
      depth, hasChildren: kids.length > 0, expanded,
    });
    return slot;
  };
  place(tree.rootId, 0);

  const maxX = nodes.reduce((m, n) => Math.max(m, n.x), 0);
  return { nodes, edges, width: maxX + NODE_W, height: maxDepth * (nodeHeight + GAP_Y) + nodeHeight, nodeHeight };
}

export interface InitialViewport {
  x: number;
  y: number;
  zoom: number;
}

/** Below this zoom the whole tree is unreadable, so the view centers on the current account instead. */
const MIN_FIT_ZOOM = 0.5;
const FALLBACK_ZOOM = 0.8;
const FIT_PADDING = 24;

/**
 * Explicit first viewport for a container of `width` x `height` px (the graph is expanded by
 * default, so it frames the WHOLE tree: zoom-to-fit, never above 1, centered). When fitting
 * would need less than `MIN_FIT_ZOOM` the tree is too big to read at once, so it centers on
 * the current account at `FALLBACK_ZOOM` instead.
 */
export function computeInitialViewport(layout: HierarchyLayout, currentId: string, width: number, height: number): InitialViewport {
  const centerAt = (cx: number, cy: number, zoom: number): InitialViewport => ({
    x: width / 2 - cx * zoom,
    y: height / 2 - cy * zoom,
    zoom,
  });
  const fit = Math.min(width / (layout.width + FIT_PADDING * 2), height / (layout.height + FIT_PADDING * 2), 1);
  if (fit >= MIN_FIT_ZOOM) return centerAt(layout.width / 2, layout.height / 2, fit);
  const current = layout.nodes.find(n => n.id === currentId) ?? layout.nodes[0];
  return centerAt((current?.x ?? 0) + NODE_W / 2, (current?.y ?? 0) + layout.nodeHeight / 2, FALLBACK_ZOOM);
}
